import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const FLUSH_INTERVAL_MS = 10_000; // flush every 10 seconds
const MAX_TRACKED_GAP_SECONDS = 60;

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export function useActiveTimeTracker() {
  const { user } = useAuth();
  const activeSecondsRef = useRef(0);
  const lastTickRef = useRef<number | null>(null);
  const visibleRef = useRef(!document.hidden);
  const accessTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user) return;

    const updateAccessToken = (token: string | null) => {
      accessTokenRef.current = token;
    };

    void supabase.auth.getSession().then(({ data }) => {
      updateAccessToken(data.session?.access_token ?? null);
    });

    const signedOutRef = { current: false };

    const {
      data: { subscription: authSubscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      updateAccessToken(session?.access_token ?? null);
      if (event === 'SIGNED_OUT' || !session) {
        signedOutRef.current = true;
        activeSecondsRef.current = 0;
      }
    });

    const addElapsedActiveSeconds = () => {
      const now = Date.now();
      if (visibleRef.current && lastTickRef.current) {
        const delta = Math.floor((now - lastTickRef.current) / 1000);
        if (delta > 0 && delta < MAX_TRACKED_GAP_SECONDS) {
          activeSecondsRef.current += delta;
        }
      }
      lastTickRef.current = now;
    };

    const getAccessToken = () => {
      if (accessTokenRef.current) return accessTokenRef.current;
      const storageKey = `sb-${SUPABASE_URL?.split('//')[1]?.split('.')[0]}-auth-token`;
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      try {
        return JSON.parse(raw)?.access_token ?? null;
      } catch {
        return null;
      }
    };

    const tick = () => {
      addElapsedActiveSeconds();
    };

    // Regular async flush for periodic saves (while page is active)
    const flush = async () => {
      addElapsedActiveSeconds();
      const seconds = activeSecondsRef.current;
      if (seconds <= 0) return;
      if (signedOutRef.current || !accessTokenRef.current) return;
      activeSecondsRef.current = 0;

      try {
        const { error } = await supabase.from('user_active_time').insert({
          user_id: user.id,
          seconds,
        });
        if (error) {
          const msg = (error.message || '').toLowerCase();
          const isAuthErr =
            msg.includes('jwt') ||
            msg.includes('row-level security') ||
            msg.includes('row level security');
          // If auth is broken, don't accumulate - the session will be cleared elsewhere.
          if (!isAuthErr) {
            activeSecondsRef.current += seconds;
          }
        }
      } catch {
        activeSecondsRef.current += seconds;
      }

    };

    // Keepalive flush that works on page unload (supports auth headers)
    const keepaliveFlush = () => {
      addElapsedActiveSeconds();
      const seconds = activeSecondsRef.current;
      if (seconds <= 0) return;
      if (signedOutRef.current) return;
      const accessToken = getAccessToken();
      if (!accessToken || !SUPABASE_URL || !SUPABASE_KEY) return;

      const url = `${SUPABASE_URL}/rest/v1/user_active_time`;
      const body = JSON.stringify({ user_id: user.id, seconds });
      activeSecondsRef.current = 0;

      try {
        void fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_KEY || '',
            'Authorization': `Bearer ${accessToken}`,
            'Prefer': 'return=minimal',
          },
          body,
          keepalive: true,
        }).catch(() => {
          activeSecondsRef.current += seconds;
        });
      } catch {
        activeSecondsRef.current += seconds;
      }
    };

    const onVisibilityChange = () => {
      visibleRef.current = !document.hidden;
      if (document.hidden) {
        keepaliveFlush();
      } else {
        lastTickRef.current = Date.now();
      }
    };

    lastTickRef.current = Date.now();

    const tickInterval = setInterval(tick, 1000);
    const flushInterval = setInterval(flush, FLUSH_INTERVAL_MS);

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('beforeunload', keepaliveFlush);
    window.addEventListener('pagehide', keepaliveFlush);

    return () => {
      clearInterval(tickInterval);
      clearInterval(flushInterval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('beforeunload', keepaliveFlush);
      window.removeEventListener('pagehide', keepaliveFlush);
      authSubscription.unsubscribe();
      void flush();
    };
  }, [user]);
}
